import { useCallback, useMemo, useRef, useState } from 'react';

import { DATE_DISPLAY_FORMATS, TIME_DISPLAY_FORMATS } from '@commons/DatePickers/constants';

import { parseDeliveryDate, parseDeliveryTime } from '../utils/externalOrderHeaderFields';

/** The delivery values of an order as the route payload carries them. */
const formatDeliveryPayload = (deliveryDate, deliveryTime) => ({
  deliveryDate: deliveryDate?.format(DATE_DISPLAY_FORMATS.DASHED_YEAR_MONTH_DAY) ?? null,
  deliveryTime: deliveryTime?.format(TIME_DISPLAY_FORMATS.SLASHED_HOUR_MINUTE) ?? null,
});

/**
 * Delivery date and time edition of an order past the draft status (COR-1771). The header fields
 * are read-only there by default: the `Modifier la date de livraison` action re-enables the two
 * delivery ones, seeded with the persisted values. The page save button stays asleep until one of
 * the two values actually moves, and a save — `deliveryPayload` on the dedicated route — closes the
 * page towards the list, as every other save of the detail.
 *
 * @param {Object} externalOrder - The order as the detail service shapes it
 *
 * @returns {Object} The edition state, its field handlers and the payload to save
 */
export const useOrderDeliveryEdition = (externalOrder) => {
  const [isEditingDelivery, setIsEditingDelivery] = useState(false);
  const [editedDeliveryDate, setEditedDeliveryDate] = useState(null);
  const [editedDeliveryTime, setEditedDeliveryTime] = useState(null);

  const startDeliveryEdition = useCallback(() => {
    setEditedDeliveryDate(parseDeliveryDate(externalOrder.deliveryDate));
    setEditedDeliveryTime(parseDeliveryTime(externalOrder.deliveryTime));
    setIsEditingDelivery(true);
  }, [externalOrder.deliveryDate, externalOrder.deliveryTime]);

  const stopDeliveryEdition = useCallback(() => {
    setIsEditingDelivery(false);
  }, []);

  /**
   * An emptied or half-typed field hands over an invalid moment (the time picker) or null (the date
   * picker), and must leave the field empty rather than keep the previous value — a date is then
   * required to save, where an emptied time simply clears it.
   */
  const handleChangeDeliveryDate = useCallback((newDeliveryDate) => {
    setEditedDeliveryDate(newDeliveryDate?.isValid() ? newDeliveryDate : null);
  }, []);

  const handleChangeDeliveryTime = useCallback((newDeliveryTime) => {
    setEditedDeliveryTime(newDeliveryTime?.isValid() ? newDeliveryTime : null);
  }, []);

  const deliveryPayload = useMemo(
    () => formatDeliveryPayload(editedDeliveryDate, editedDeliveryTime),
    [editedDeliveryDate, editedDeliveryTime],
  );

  const persistedDelivery = useMemo(
    () =>
      formatDeliveryPayload(
        parseDeliveryDate(externalOrder.deliveryDate),
        parseDeliveryTime(externalOrder.deliveryTime),
      ),
    [externalOrder.deliveryDate, externalOrder.deliveryTime],
  );

  const hasDeliveryChange =
    isEditingDelivery &&
    (deliveryPayload.deliveryDate !== persistedDelivery.deliveryDate ||
      deliveryPayload.deliveryTime !== persistedDelivery.deliveryTime);

  const isMissingDeliveryDate = isEditingDelivery && !editedDeliveryDate;

  /**
   * The delivery to carry with a status transition, or `undefined` when it is untouched — which
   * spares the transition a useless save. Going through a ref keeps the transition handlers stable,
   * exactly as `getPendingComment` does for the comment.
   */
  const pendingDeliveryRef = useRef(null);

  pendingDeliveryRef.current = hasDeliveryChange && !isMissingDeliveryDate ? deliveryPayload : null;

  const getPendingDelivery = useCallback(() => pendingDeliveryRef.current ?? undefined, []);

  return {
    isEditingDelivery,
    editedDeliveryDate,
    editedDeliveryTime,
    deliveryPayload,
    hasDeliveryChange,
    isMissingDeliveryDate,
    getPendingDelivery,
    startDeliveryEdition,
    stopDeliveryEdition,
    handleChangeDeliveryDate,
    handleChangeDeliveryTime,
  };
};
